'use client';

import { Box } from '@chakra-ui/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useLayoutEffect, useRef } from 'react';

/**
 * The page content inside the layout's scroll container, at least as tall as the container so that the footer stays
 * below the fold and pages that render their content in stages do not push a visible footer (a layout shift).
 *
 * The min height lets even a page that fits scroll by the footer's height, and the container outlives client-side
 * navigations. So that a navigation lands where it would without the min height, it drops the part of the offset that
 * lay beyond the limit without the min height when the user last scrolled (0 after a page that fits), and clamps the
 * offset to that limit for the new page. Within a page, the offset is left alone, so the user can keep reading the
 * footer while content updates.
 */
export const PageContentBox: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const slackFreeScrollTopRef = useRef<number>(undefined);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const content = contentRef.current;
    const container = box?.parentElement;
    if (!box || !content || !container) return;

    const onScroll = (): void => {
      const max = getMaxScrollTopWithoutMinHeight(box, content, container);
      slackFreeScrollTopRef.current = container.scrollTop > max ? max : undefined;
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <Box ref={boxRef} minH="full" pb={16} pt={8}>
      {/* `useSearchParams` needs a Suspense boundary on statically rendered pages. */}
      <Suspense>
        <NavigationListener boxRef={boxRef} contentRef={contentRef} slackFreeScrollTopRef={slackFreeScrollTopRef} />
      </Suspense>
      <div ref={contentRef}>{children}</div>
    </Box>
  );
};

/** Lands each navigation, a change of the pathname or the query, where it would land without the min height. */
const NavigationListener: React.FC<{
  boxRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  slackFreeScrollTopRef: React.RefObject<number | undefined>;
}> = ({ boxRef, contentRef, slackFreeScrollTopRef }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  useLayoutEffect(() => {
    const box = boxRef.current;
    const content = contentRef.current;
    const container = box?.parentElement;
    const slackFreeScrollTop = slackFreeScrollTopRef.current;
    slackFreeScrollTopRef.current = undefined;
    if (!box || !content || !container) return;
    const scrollTop = Math.min(
      slackFreeScrollTop ?? container.scrollTop,
      getMaxScrollTopWithoutMinHeight(box, content, container)
    );
    if (container.scrollTop !== scrollTop) container.scrollTop = scrollTop;
  }, [boxRef, contentRef, pathname, searchParams, slackFreeScrollTopRef]);
  return;
};

function getMaxScrollTopWithoutMinHeight(box: HTMLElement, content: HTMLElement, container: HTMLElement): number {
  const naturalBoxHeight = content.offsetHeight + getVerticalPadding(box);
  return Math.max(0, container.scrollHeight - box.offsetHeight + naturalBoxHeight - container.clientHeight);
}

function getVerticalPadding(element: HTMLElement): number {
  const style = getComputedStyle(element);
  return Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom);
}
